import React, { useState } from 'react';
import { ISocialDataProps } from '../../actions/social';
import { ILogInUserApiResponseProps } from '../../api/login';
import { ILookUpUserApiResponseProps } from '../../api/lookup';
import { VERIFICATION_STATUSES } from '../../constants/status';

export interface IUserContextInitialState {
    user?: ILogInUserApiResponseProps | {};
    tosSocialAccepted?: boolean;
    socialProvider?: string;
    socialData?: ISocialDataProps | {};
    socialLinkedAccount?: any;
    tosSocialStatus?: string;
    linkedAccountSuccessUrl?: string;
    lookupResponse?: ILookUpUserApiResponseProps | null;
    lookedAccount?: string;
    existentAccount?: string;
    socialConnected?: string[];
    passwordlessLoginStatus?: string;
    emailVerificationStatus?: string;
    verificationCode?: string;
}
export interface IUserContextState {
    user: ILogInUserApiResponseProps | {};
    lookup: ILookupState;
    tosSocialAccepted: boolean;
    socialProvider: string;
    socialData: any;
    socialLinkedAccount: any;
    tosSocialStatus: string;
    linkedAccountSuccessUrl: string;
    passwordlessLoginStatus: string;
    emailVerificationStatus: string;
    verificationCode: string;
}

export interface IUserContext {
    methods: {
        setUser: (response: ILogInUserApiResponseProps) => void;
        setUserAccountTypes: (accountType: AccountTypes) => void;
        setVerificationCode: (value: string) => void;
        lookup: {
            setLookupResponse: (response: ILookUpUserApiResponseProps) => void;
            setLookedAccount: (value: string) => void;
            setExistentAccount: (value: string) => void;
            setSocialConnected: (value: string[]) => void;
        };
        setTosSocialAccepted: (value: boolean) => void;
        setSocialProvider: (value: string) => void;
        setSocialData: (value: ISocialDataProps | {}) => void;
        setSocialLinkedAccount: (value: string) => void;
        setTosSocialStatus: (value: string) => void;
        setLinkedAccountSuccessUrl: (value: string) => void;
        setPasswordlessLoginStatus: (value: string) => void;
        setEmailVerificationStatus: (value: string) => void;
    };
    state: IUserContextState;
}

export enum AccountTypes {
    softAccount = 'softAccount',
    hardAccount = 'hardAccount',
    socialAccount = 'socialAccount',
}

interface ILookupState {
    lookupResponse: ILookUpUserApiResponseProps | null;
    lookedAccount?: string;
    existentAccount?: string;
    socialConnected?: string[];
}

export const useUserContext = () => {
    const value = React.useContext(UserContext);

    if (!value) {
        throw new Error('useUserContext must be used within FrontendContext');
    }

    return value;
};

const UserContext = React.createContext<any>(null);

interface IUserProps extends ILogInUserApiResponseProps {
    accountType?: AccountTypes;
}
const UserContextProvider = ({
    children,
    initialState: {
        user: _user,
        lookupResponse: _lookupResponse,
        lookedAccount: _lookedAccount,
        existentAccount: _existentAccount,
        socialConnected: _socialConnected,
        tosSocialAccepted: _tosSocialAccepted,
        socialProvider: _socialProvider,
        socialData: _socialData,
        socialLinkedAccount: _socialLinkedAccount,
        tosSocialStatus: _tosSocialStatus,
        linkedAccountSuccessUrl: _linkedAccountSuccessUrl,
        passwordlessLoginStatus: _passwordlessLoginStatus,
        emailVerificationStatus: _emailVerificationStatus,
        verificationCode: _verificationCode,
    } = {},
}: {
    children: React.ReactNode;
    initialState?: Partial<IUserContextInitialState>;
}) => {
    const [user, _setUser] = useState<Partial<IUserProps>>(_user || {});

    // Lookup state
    const [lookupResponse, setLookupResponse] =
        useState<ILookUpUserApiResponseProps | null>(_lookupResponse ?? null);
    const [lookedAccount, setLookedAccount] = useState<string>(
        _lookedAccount ?? '',
    );
    const [existentAccount, setExistentAccount] = useState<string>(
        _existentAccount ?? '',
    );
    /**
     * Social connected should be an array of string including only the social
     * name, not the _login part that comes on some request.
     * Example: ['facebook', 'apple']
     */
    const [socialConnected, setSocialConnected] = useState<string[]>(
        _socialConnected ?? [],
    );
    const [tosSocialAccepted, setTosSocialAccepted] = useState<boolean>(
        _tosSocialAccepted ?? false,
    );
    const [socialProvider, setSocialProvider] = useState<string>(
        _socialProvider ?? '',
    );
    // Before this was an object with this form {facebook: {fbData}, google: {gData}, apple: {aData}}
    const [socialData, setSocialData] = useState<ISocialDataProps | {}>(
        _socialData ?? {},
    );
    const [socialLinkedAccount, setSocialLinkedAccount] = useState<any>(
        _socialLinkedAccount ?? {},
    );
    const [tosSocialStatus, setTosSocialStatus] = useState<any>(
        _tosSocialStatus ?? {},
    );

    const [passwordlessLoginStatus, setPasswordlessLoginStatus] =
        React.useState<string>(_passwordlessLoginStatus ?? '');

    // Linked state
    const [linkedAccountSuccessUrl, setLinkedAccountSuccessUrl] =
        useState<string>(_linkedAccountSuccessUrl ?? '');

    const setUser = (response: ILogInUserApiResponseProps) => {
        /**
         * Only keeps the account type for the user that passed trough lookup
         */
        const isEmailAmongLookup = response?.emails
            ?.map(({ email }: { email: string }) => email)
            .includes(existentAccount);
        const conditionalProps: Partial<IUserProps> = {};

        if (isEmailAmongLookup) {
            conditionalProps.accountType = user.accountType;
        }

        _setUser({
            ...conditionalProps,
            ...response,
        });
    };

    const setUserAccountTypes = (accountType: AccountTypes) => {
        _setUser({
            ...user,
            accountType,
        });
    };

    // Signup email verification
    const [emailVerificationStatus, setEmailVerificationStatus] =
        React.useState<string>(
            _emailVerificationStatus ?? VERIFICATION_STATUSES.empty,
        );

    const [verificationCode, setVerificationCode] = useState<string>(
        _verificationCode ?? '',
    );
    const exportedValues: IUserContext = {
        methods: {
            setUser,
            setUserAccountTypes,
            setVerificationCode,
            lookup: {
                setLookupResponse,
                setLookedAccount,
                setExistentAccount,
                setSocialConnected,
            },
            setTosSocialAccepted,
            setSocialProvider,
            setSocialData,
            setSocialLinkedAccount,
            setTosSocialStatus,
            setLinkedAccountSuccessUrl,
            setPasswordlessLoginStatus,
            setEmailVerificationStatus,
        },
        state: {
            user,
            lookup: {
                lookupResponse,
                lookedAccount,
                existentAccount,
                socialConnected,
            },
            tosSocialAccepted,
            socialProvider,
            socialData,
            socialLinkedAccount,
            tosSocialStatus,
            linkedAccountSuccessUrl,
            passwordlessLoginStatus,
            emailVerificationStatus,
            verificationCode,
        },
    };
    return (
        <UserContext.Provider value={exportedValues}>
            {children}
        </UserContext.Provider>
    );
};

export default {
    Context: UserContext,
    Consumer: UserContext.Consumer,
    Provider: UserContextProvider,
};
